import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    IconButton,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DeleteIcon from '@mui/icons-material/Delete'
import { useNavigate } from 'react-router-dom'
import Dropdown from '../../../components/Dropdown'
import { useQuery } from '../../../contexts/QueryContext'
import { useDatabase } from '../../../contexts/DatabaseContext'
import { useDocumentCreation } from '../../../contexts/DocumentCreationContext'
import { v4 as uuidv4 } from 'uuid'
import { ContinueButton } from '../../../components/Buttons'

const DatabaseQuerySelection = ({ onBack, onConfirm }) => {
    const navigate = useNavigate()
    const { queries, setQueries } = useQuery()
    const { databases } = useDatabase()
    const { document, updateDocument } = useDocumentCreation()
    
    const [expandedSections, setExpandedSections] = useState({})

    // Transform document.selectedSections into the format needed for this UI
    const getSectionsWithDatabaseRules = () => {
        if (!document || !document.selectedSections || document.selectedSections.length === 0) {
            return []
        }

        const queriesWithVariables = document.queries_with_variables || []
        console.log('queriesWithVariables', queriesWithVariables)

        return document.selectedSections
            .filter(section => {
                // Only include sections that have fixed_elements with Database source_type
                return section.fixed_elements && section.fixed_elements.some(element => 
                    element.source_type === 'Database'
                )
            })
            .map(section => {
                // Find existing queries for this section
                const existingQueries = queriesWithVariables.filter(q => 
                    q.section_id === section.id
                ) || []

                // Create rules from fixed_elements that have Database source_type
                const rules = section.fixed_elements
                    .filter(element => element.source_type === 'Database')
                    .map((element, index) => {
                        const existingQuery = existingQueries.find(q => q.element_index === index)
                        return {
                            id: existingQuery?.id || uuidv4(),
                            element_index: index,
                            content_type: element.content_type || '',
                            source_type: element.source_type || '',
                            query: existingQuery?.query || null,
                            variables: existingQuery?.variables || {}
                        }
                    })

                return {
                    id: section.id,
                    name: section.section_name || section.template_section_name || `Section ${section.id}`,
                    checked: true, // Always checked since these are selected sections
                    rules: rules
                }
            })
    }

    // Function to extract variables from SQL query
    const extractVariablesFromQuery = (sql) => {
        if (!sql) return []

        // Simple regex to catch {variable} or (variable) or :variable etc.
        const regexes = [
            /\{([^}]+)\}/g,
            /\(([^)]+)\)/g,
            /:(\w+)/g,
            /@(\w+)/g,
            /\$\{([^}]+)\}/g,
            /\{\{([^}]+)\}\}/g,
        ]

        const vars = new Set()
        regexes.forEach(r => {
            let m
            while ((m = r.exec(sql)) !== null) {
                const v = m[1].trim()
                if (v && isNaN(v)) {
                    vars.add(v)
                }
            }
        })
        return Array.from(vars)
    }

    const [sections, setSections] = useState([])

    // Initialize sections from document data
    useEffect(() => {
        console.log('Document:', document)
        console.log('Selected sections:', document.selectedSections)
        console.log('Queries with variables:', document.queries_with_variables)
        
        const sectionsWithRules = getSectionsWithDatabaseRules()
        console.log('Sections with database rules:', sectionsWithRules)
        setSections(sectionsWithRules)
    }, [document.selectedSections, document.queries_with_variables])

    // Content type options
    const contentTypeOptions = [
        { id: 1, value: 'Text', label: 'Text' },
        { id: 2, value: 'Table', label: 'Table' },
        { id: 3, value: 'Figure', label: 'Figure' }
    ]

    // Source type options
    const sourceTypeOptions = [
        { id: 1, value: 'Database', label: 'Database' },
        { id: 2, value: 'Document', label: 'Document' },
        { id: 3, value: 'Template Text', label: 'Template Text' },
        { id: 4, value: 'Content Library', label: 'Content Library' }
    ]

    // Get query options from QueryContext
    const queryOptions = queries.map(query => ({
        id: query.id,
        value: query.query_name,
        label: query.query_name,
        query_sql: query.query_sql || query.sql || ''
    }))

    // Mock variable options
    const variableOptions = [
        { id: 1, value: 'ABC-124', label: 'ABC-124' },
        { id: 2, value: 'XYZ-789', label: 'XYZ-789' },
        { id: 3, value: 'DEF-456', label: 'DEF-456' },
        { id: 4, value: 'EXP 667', label: 'EXP 667' },
        { id: 5, value: 'EXP 680', label: 'EXP 680' }
    ]

    const handleBack = () => {
        if (onBack) {
            onBack()
        } else {
            navigate(-1)
        }
    }

    const handleSectionCheck = (sectionId) => {
        setSections(prev => prev.map(section => 
            section.id === sectionId 
                ? { ...section, checked: !section.checked }
                : section
        ))
    }

    const handleAccordionChange = (sectionId) => (event, isExpanded) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: isExpanded
        }))
    }

    const handleRuleChange = (sectionId, ruleId, field, value, variableName = null) => {
        // Update local state
        setSections(prev => prev.map(section => 
            section.id === sectionId 
                ? {
                    ...section,
                    rules: section.rules.map(rule => {
                        if (rule.id !== ruleId) return rule
                        if (field === 'variables' && variableName) {
                            return {
                                ...rule,
                                variables: {
                                    ...rule.variables,
                                    [variableName]: value,
                                },
                            }
                        }
                        return { ...rule, [field]: value }
                    })
                }
                : section
        ))

        // Update document context
        const currentQueries = document.queries_with_variables || []
        const existingQueryIndex = currentQueries.findIndex(q => 
            q.section_id === sectionId && q.id === ruleId
        )

        let updatedQueries
        if (existingQueryIndex >= 0) {
            // Update existing query
            updatedQueries = [...currentQueries]
            if (field === 'variables' && variableName) {
                updatedQueries[existingQueryIndex] = {
                    ...updatedQueries[existingQueryIndex],
                    variables: {
                        ...updatedQueries[existingQueryIndex].variables,
                        [variableName]: value,
                    },
                }
            } else {
                updatedQueries[existingQueryIndex] = {
                    ...updatedQueries[existingQueryIndex],
                    [field]: value,
                }
            }
        } else {
            // Create new query entry
            const section = sections.find(s => s.id === sectionId)
            const rule = section?.rules.find(r => r.id === ruleId)
            
            const newQuery = {
                id: ruleId,
                section_id: sectionId,
                element_index: rule?.element_index || 0,
                content_type: field === 'content_type' ? value : rule?.content_type || '',
                source_type: field === 'source_type' ? value : rule?.source_type || '',
                query: field === 'query' ? value : rule?.query || null,
                variables: field === 'variables' && variableName ? { [variableName]: value } : rule?.variables || {}
            }
            updatedQueries = [...currentQueries, newQuery]
        }

        updateDocument({
            ...document,
            queries_with_variables: updatedQueries
        })
    }

    const removeRule = (sectionId, ruleId) => {
        // Update local state
        setSections(prev => prev.map(section => 
            section.id === sectionId 
                ? {
                    ...section,
                    rules: section.rules.filter(rule => rule.id !== ruleId)
                }
                : section
        ))

        // Update document context
        const updatedQueries = (document.queries_with_variables || []).filter(q => 
            !(q.section_id === sectionId && q.id === ruleId)
        )

        updateDocument({
            ...document,
            queries_with_variables: updatedQueries
        })
    }

    const renderRule = (section, rule, ruleIndex) => {
        const selectedQuery = queryOptions.find(q => q.value === rule.query?.query_name || q.value === rule.query?.value)
        const extractedVars = selectedQuery ? extractVariablesFromQuery(selectedQuery.query_sql) : []
        
        return (
            <Box
                key={rule.id}
                sx={{
                    mb: ruleIndex < section.rules.length - 1 ? '24px' : 0,
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)'
                }}
            >
                {/* Rule Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: '16px'
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#374151',
                            lineHeight: '20px'
                        }}
                    >
                        Rule {ruleIndex + 1}
                    </Typography>
                    <IconButton
                        onClick={() => removeRule(section.id, rule.id)}
                        sx={{
                            padding: '4px',
                            color: '#9CA3AF',
                            '&:hover': {
                                color: '#1F2937'
                            }
                        }}
                    >
                        <DeleteIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                </Box>

                {/* Content Type Dropdown */}
                <Dropdown
                    value={rule.content_type || ''}
                    placeholder="Select content type"
                    options={contentTypeOptions}
                    onChange={(e) => handleRuleChange(section.id, rule.id, 'content_type', e.target.value)}
                    sx={{ mb: '12px' }}
                />

                {/* Source Type Dropdown */}
                <Dropdown
                    value={rule.source_type || ''}
                    placeholder="Select source type"
                    options={sourceTypeOptions}
                    onChange={(e) => handleRuleChange(section.id, rule.id, 'source_type', e.target.value)}
                    sx={{ mb: '12px' }}
                />

                {/* Query Dropdown - only show if source type is Database */}
                {rule.source_type === 'Database' && (
                    <>
                        <Dropdown
                            value={rule.query?.query_name || ''}
                            placeholder="Select query"
                            options={queryOptions}
                            onChange={(e) => {
                                const selectedQuery = queryOptions.find(q => q.value === e.target.value)
                                handleRuleChange(section.id, rule.id, 'query', {
                                    query_name: selectedQuery.value,
                                    query_sql: selectedQuery.query_sql,
                                    value: selectedQuery.value
                                })
                            }}
                            sx={{ mb: '12px' }}
                        />

                        {/* Query Display Container */}
                        {console.log('selectedQuery', selectedQuery)}
                        {selectedQuery && (
                            <Box sx={{
                                width: '468px',
                                maxHeight: '300px',
                                borderRadius: '8px',
                                padding: '16px',
                                backgroundColor: '#F9FAFB',
                                border: 'none',
                                boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
                                fontSize: '12px',
                                lineHeight: '20px',
                                color: '#374151',
                                mb: '12px',
                                overflow: 'auto',
                                fontFamily: 'monospace',
                                // Hide scrollbar while maintaining scroll functionality
                                scrollbarWidth: 'none', // Firefox
                                msOverflowStyle: 'none', // IE and Edge
                                '&::-webkit-scrollbar': {
                                    display: 'none', // Chrome, Safari, Opera
                                }
                            }}>
                                {selectedQuery.query_sql}
                            </Box>
                        )}

                        {/* Variable Dropdowns */}
                        {extractedVars.length > 0 && (
                            extractedVars.map((varName, idx) => (
                                <Box key={varName} sx={{ mt: idx === 0 ? 0 : 2 }}>
                                    <Dropdown
                                        value={rule.variables?.[varName] || ''}
                                        placeholder={`Select value for ${varName}`}
                                        options={variableOptions}
                                        onChange={(e) => handleRuleChange(section.id, rule.id, 'variables', e.target.value, varName)}
                                    />
                                </Box>
                            ))
                        )}
                    </>
                )}
            </Box>
        )
    }

    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%', 
            padding: '40px',
            paddingTop: '120px', // Account for fixed stepper
            '@media (max-width: 768px)': {
                paddingTop: '100px',
            }
        }}>
            <Box sx={{
                position: 'fixed',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 999,
                width: 'clamp(600px, 60vw, 900px)',
                '@media (max-width: 768px)': {
                    width: 'calc(100vw - 32px)',
                    left: '16px',
                    transform: 'none',
                }
            }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <IconButton
                        onClick={handleBack}
                        sx={{
                            padding: '6px',
                            marginLeft: '-12px',
                            '&:hover': {
                                backgroundColor: '#F3F4F6',
                            },
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: '20px', color: '#374151' }} />
                    </IconButton>
                    <Box>
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                fontSize: '24px',
                                fontWeight: 600,
                                color: '#111827',
                                mb: 1
                            }}
                        >
                            Define queries for 
                            database rules in these sections
                        </Typography>
                    </Box>
                </Box>

                {/* Sections */}
                <Box sx={{ 
                    width: '100%',
                    minWidth: 'unset',
                    '@media (min-width: 769px)': {
                        minWidth: '600px',
                    }
                }}>
                {sections.length === 0 ? (
                    <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB'
                    }}>
                        <Typography sx={{ 
                            fontSize: '14px',
                            color: '#6B7280',
                            mb: 1
                        }}>
                            No sections with database rules found
                        </Typography>
                        <Typography sx={{ 
                            fontSize: '12px',
                            color: '#9CA3AF'
                        }}>
                            Make sure you have selected sections with database source types in the previous steps.
                        </Typography>
                    </Box>
                ) : (
                    sections.map((section) => (
                        <Box key={section.id} sx={{ mb: 2 }}>
                            {/* Section Header with Checkbox */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Checkbox
                                    checked={section.checked}
                                    onChange={() => handleSectionCheck(section.id)}
                                    sx={{
                                        padding: '4px',
                                        mr: 1,
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 18,
                                            color: section.checked ? '#4F46E5' : '#D1D5DB'
                                        },
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        }
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: '#374151'
                                    }}
                                >
                                    {section.name}
                                </Typography>
                            </Box>

                            {/* Section Rules */}
                            {section.checked && section.rules.length > 0 && (
                                <Box sx={{ ml: 4 }}>
                                    {section.rules.map((rule, ruleIndex) => 
                                        renderRule(section, rule, ruleIndex)
                                    )}
                                </Box>
                            )}
                        </Box>
                    ))
                )}
                </Box>
                <ContinueButton
                    onClick={onConfirm}
                    text="Continue"
                    width="120px"
                    backgroundColor="#4F46E5"
                    fontColor="#FFFFFF"
                />
            </Box>
        </Box>
    )
}

export default DatabaseQuerySelection
